import useToasts from '@leaflink/stash/useToasts';
import { storeToRefs } from 'pinia';
import { h } from 'vue';
import { RouteRecordRaw, RouterLink } from 'vue-router';

import { LAYOUTS, ROUTE_NAMES, SPLIT_NAMES } from '@/constants';
import { RouteTab } from '@/models/routing';
import { useAppConfigStore } from '@/piniaStores/appConfig/appConfig.store';
import { useAuthStore } from '@/piniaStores/auth/auth.store';
import { useCompanyStore } from '@/piniaStores/company/company.store';
import { useUserStore } from '@/piniaStores/user/user.store';
import i18n, { loadTranslations } from '@/plugins/i18n';
import { loadSegmentAnalytics } from '@/segment';
import { getAuth0Client, initAuth0 } from '@/services/auth';

import { toNonVueRoute } from './utils';
const Discover = () => import('@/pages/Shop/Discover/Discover.vue');
const PurchasingView = () => import('@/pages/Shop/Purchasing/index.vue');
const PurchaseHistoryNew = () => import('@/pages/Shop/Purchasing/PurchaseHistoryNew.vue');
const Shop = () => import('@/pages/Shop/Shop.vue');
const ShopBrands = () => import('@/pages/Shop/Brands/Brands.vue');
const ShopDeals = () => import('@/pages/Shop/Deals/Deals.vue');
const ShopProducts = () => import('@/pages/Shop/Products/Products.vue');
const StoreFavorites = () => import('@/pages/Shop/Purchasing/StoreFavorites.vue');

// Menus
const BrandMenu = () => import('@/pages/Shop/BrandMenu/BrandMenu.vue');
const BrandMenuLeaderboard = () => import('@/pages/Shop/BrandMenu/BrandMenuLeaderboard.vue');
const ExpressMenu = () => import('@/pages/Shop/ExpressMenu/ExpressMenu.vue');
const PreviewBrandMenu = () => import('@/pages/Shop/PreviewBrandMenu/PreviewBrandMenu.vue');
const PreviewMenu = () => import('@/pages/Shop/PreviewMenu/PreviewMenu.vue');
const PublicMenu = () => import('@/pages/Shop/PublicMenu/PublicMenu.vue');
const VendorMenu = () => import('@/pages/Shop/VendorMenu/VendorMenu.vue');

async function fetchShopTranslations() {
  await loadTranslations('shop');
}

function fetchShopAndCartTranslations(_to, _from, next) {
  Promise.all([
    loadTranslations('shop'),
    loadTranslations('cart'),
    loadTranslations('productRecommendation'),
    loadTranslations('recentlyPurchased'),
  ]).then(() => next()); // The PaymentsBadge references cart.json translations
}

function shopBuyerLicenseGuard(_to, from, next) {
  const { getIsSplitEnabled } = storeToRefs(useUserStore());
  const { activeCompany } = storeToRefs(useCompanyStore());
  const hasActiveLicense = activeCompany.value?.licenses?.filter((license) => license?.warning !== 'expired').length;
  if (hasActiveLicense || !getIsSplitEnabled.value(SPLIT_NAMES.BUYER_METRC_ONBOARDING)) {
    return next();
  }

  const params = {
    company: activeCompany.value?.slug,
  };

  useToasts({
    timeout: 10000,
  }).error(() => {
    return h('span', [
      h('span', `${i18n.t('shop.addLicenseError')} `),
      h(
        RouterLink,
        { to: { name: ROUTE_NAMES.LICENSES, params } },
        {
          default: () => i18n.t('shop.clickHereToManageLicenses'),
        },
      ),
    ]);
  });

  if (from.path === '/') {
    return next({
      name: ROUTE_NAMES.COMPANY_DASHBOARD,
      params,
    });
  }

  return next(false);
}

export function purchaseListTabRoutes(): Readonly<RouteTab[]> {
  return Object.freeze([
    {
      to: { name: ROUTE_NAMES.STORE_FAVORITES },
      label: i18n.t('shop.storeFavorites.title'),
    },
    {
      to: { name: ROUTE_NAMES.PURCHASE_HISTORY_NEW },
      label: i18n.t('shop.purchaseHistory'),
    },
  ]);
}

const companyRoutes: RouteRecordRaw[] = [
  // Legacy Routes
  toNonVueRoute({ path: 'inventory/products/:productId/', name: ROUTE_NAMES.PRODUCT_DETAILS }),
  toNonVueRoute({ path: 'vendors/companies/:sellerId/', name: ROUTE_NAMES.SELLER_DETAILS }),
  toNonVueRoute({ path: 'shop/brands/:brandId/choose-license/', name: ROUTE_NAMES.CHOOSE_LICENSE }),

  // Vue Routes
  {
    path: 'shop',
    name: ROUTE_NAMES.SHOP,
    component: Shop,
    beforeEnter: [fetchShopAndCartTranslations, shopBuyerLicenseGuard],
    meta: {
      title: () => i18n.t('shop.products.heading'),
      permissions: [
        {
          rules: [
            'canPurchase',
            ({ userActiveCompany }) => Boolean(userActiveCompany?.staffId && userActiveCompany.buyer),
          ],
        },
      ],
    },
    redirect: (to) => ({ name: ROUTE_NAMES.SHOP_BRANDS, params: { company: to.params.company } }),
    children: [
      {
        path: 'brands',
        name: ROUTE_NAMES.SHOP_BRANDS,
        component: ShopBrands,
        meta: {
          title: () => i18n.t('shop.brands.heading'),
        },
      },
      {
        path: 'products',
        name: ROUTE_NAMES.SHOP_PRODUCTS,
        component: ShopProducts,
        meta: {
          title: () => i18n.t('shop.products.heading'),
        },
      },
      {
        path: 'discover',
        name: ROUTE_NAMES.DISCOVER_PRODUCTS,
        component: Discover,
        meta: {
          title: () => i18n.t('shop.discover.title'),
          description: () => i18n.t('shop.discover.description'),
        },
      },
    ],
  },
  {
    path: 'shop/brands/:brandId',
    name: ROUTE_NAMES.BRAND_MENU,
    component: () =>
      useUserStore().getIsSplitEnabled('llm_inventory_insights_cutover') ? BrandMenuLeaderboard() : BrandMenu(),
    props: true,
    beforeEnter: [fetchShopAndCartTranslations],
    meta: {
      segmentPageName: 'Brand Menu',
      permissions: [
        {
          rules: [
            'canPurchase',
            ({ userActiveCompany }) => Boolean(userActiveCompany?.buyer && userActiveCompany.staffId),
          ],
        },
      ],
    },
  },
  {
    path: 'brands/:brandId',
    name: ROUTE_NAMES.BRAND_DETAILS,
    component: PreviewBrandMenu,
    props: true,
    beforeEnter: [fetchShopTranslations],
    meta: {
      permissions: [{ rules: ['canPurchase', ({ userActiveCompany }) => Boolean(userActiveCompany?.buyer)] }],
    },
  },
  {
    path: 'shop/vendor/:sellerId',
    name: ROUTE_NAMES.VENDOR_MENU,
    component: VendorMenu,
    props: true,
    beforeEnter: [fetchShopTranslations],
    meta: {
      permissions: [
        {
          rules: [
            'canPurchase',
            ({ userActiveCompany }) => Boolean(userActiveCompany?.buyer && userActiveCompany.staffId),
            // TODO: Add rule for enable_vendor_menu/enable_seller_menu
          ],
        },
      ],
    },
  },
  {
    path: 'shop/deals',
    name: ROUTE_NAMES.SHOP_DEALS,
    component: ShopDeals,
    beforeEnter: [fetchShopTranslations],
    meta: {
      title: () => i18n.t('shop.deals.heading'),
      permissions: [
        {
          rules: [
            'canPurchase',
            ({ userActiveCompany }) => Boolean(userActiveCompany?.buyer && userActiveCompany.staffId),
          ],
        },
      ],
    },
  },
  {
    path: 'brands/:brandId/shop/preview',
    name: ROUTE_NAMES.SHOP_BRAND_PREVIEW,
    component: PreviewMenu,
    beforeEnter: [fetchShopTranslations],
    props: true,
    meta: {
      permissions: [{ rules: [({ userActiveCompany }) => Boolean(userActiveCompany?.staffId)] }],
    },
  },
  {
    path: 'discover',
    redirect: (to) => ({ name: ROUTE_NAMES.DISCOVER_PRODUCTS, params: { company: to.params.company } }),
  },
  // new purchase list and history, now under company slug
  {
    path: 'purchasing/',
    component: PurchasingView,
    beforeEnter: [fetchShopTranslations],
    meta: {
      title: () => i18n.t('shop.buyItAgain.title'),
      tabs: purchaseListTabRoutes,
      permissions: [{ rules: ['canPurchase', ({ userActiveCompany }) => Boolean(userActiveCompany?.staffId)] }],
    },
    children: [
      {
        path: 'favorites',
        name: ROUTE_NAMES.STORE_FAVORITES,
        component: StoreFavorites,
        meta: {
          title: () => i18n.t('shop.buyItAgain.title'),
          description: () => i18n.t('shop.buyItAgain.description'),
        },
      },
      {
        path: 'history',
        name: ROUTE_NAMES.PURCHASE_HISTORY_NEW,
        component: PurchaseHistoryNew,
        meta: {
          title: () => i18n.t('shop.buyItAgain.title'),
          description: () => i18n.t('shop.buyItAgain.description'),
        },
      },
    ],
  },
];

const baseRoutes: RouteRecordRaw[] = [
  {
    path: '/menu/:brandSlug',
    name: ROUTE_NAMES.PUBLIC_MENU,
    component: PublicMenu,
    props: true,
    beforeEnter: [fetchShopTranslations, loadSegmentAnalytics],
    meta: {
      layout: LAYOUTS.FULL,
    },
  },
  {
    path: '/express-menu/:menuSlug',
    name: ROUTE_NAMES.EXPRESS_MENU,
    component: ExpressMenu,
    beforeEnter: [expressMenuRouteGuard, fetchShopAndCartTranslations, loadSegmentAnalytics],
    meta: {
      layout: LAYOUTS.FULL,
    },
  },
];

async function expressMenuRouteGuard() {
  const userStore = useUserStore();
  const { isUserAuthenticated } = storeToRefs(userStore);

  // Check if the user is authenticated without redirecting to the login page
  await userStore.getAuthenticationStatus();

  if (!isUserAuthenticated.value || getAuth0Client()) return;

  // We can't use the standard authenticationGuard used in other routes
  // because this page can be accessed by both authenticated and unauthenticated users.
  // However, if the user is authenticated, we need to initialize Auth0
  // in order to properly track events (e.g. with Segment).
  const { unifiedConfig } = useAppConfigStore();

  if (unifiedConfig) {
    const { domain, clientId, audience } = unifiedConfig;

    await initAuth0({ domain, clientId, audience });
    await useAuthStore().login();
  }
}

export default { companyRoutes, baseRoutes };
